import { useState, useEffect } from "react";
import { Menu, X, Phone, Facebook } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { navLinks, company } from "../mock";
import { Button } from "./ui/button";
import logoFull from "../assets/logo-full.png";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (!isHome) {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const linkClass = `link-underline text-[15px] font-medium transition-colors ${
    solid ? "text-slate-700 hover:text-primary" : "text-white/90 hover:text-white"
  }`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-white/90 backdrop-blur-md shadow-[0_4px_24px_rgba(15,40,80,0.06)] border-b border-border"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8">
        <div className="flex items-center justify-between h-[64px] sm:h-[70px]">
          {/* Logo */}
          <Link to="/" className="flex items-center group" onClick={() => setOpen(false)}>
            <img
              src={logoFull}
              alt="Selekto Sähköpalvelu"
              className="h-9 sm:h-12 w-auto object-contain transition-transform group-hover:scale-[1.03]"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) =>
              l.type === "route" ? (
                <Link key={l.label} to={l.to} className={linkClass}>
                  {l.label}
                </Link>
              ) : (
                <button key={l.label} onClick={() => goToSection(l.id)} className={linkClass}>
                  {l.label}
                </button>
              )
            )}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={company.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Selekto Facebookissa"
              className="fb-pulse inline-flex items-center gap-2 rounded-full bg-[#1877F2] hover:bg-[#0e5fd8] text-white px-4 h-10 text-sm font-semibold transition-colors"
            >
              <Facebook className="w-4 h-4" fill="currentColor" />
              Facebook
            </a>
            <Button asChild className="rounded-full px-5 gap-2 shadow-lg shadow-primary/20">
              <a href={`tel:${company.phoneRaw}`}>
                <Phone className="w-4 h-4" />
                {company.phone}
              </a>
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            className={`lg:hidden grid place-items-center w-10 h-10 rounded-lg transition-colors ${
              solid ? "text-slate-800 hover:bg-secondary" : "text-white hover:bg-white/10"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Valikko"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-b border-border shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((l) =>
              l.type === "route" ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="py-3 px-3 rounded-lg text-slate-700 font-medium hover:bg-secondary hover:text-primary transition-colors"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  onClick={() => goToSection(l.id)}
                  className="text-left py-3 px-3 rounded-lg text-slate-700 font-medium hover:bg-secondary hover:text-primary transition-colors"
                >
                  {l.label}
                </button>
              )
            )}
            <Button asChild className="mt-2 rounded-full gap-2">
              <a href={`tel:${company.phoneRaw}`}>
                <Phone className="w-4 h-4" />
                {company.phone}
              </a>
            </Button>
            <a
              href={company.facebook}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-1 inline-flex items-center justify-center gap-2 py-3 px-3 rounded-full bg-[#1877F2] text-white font-semibold transition-colors"
            >
              <Facebook className="w-5 h-5" fill="currentColor" />
              Seuraa Facebookissa
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
